import { useEffect, useState } from "react";
import { Badge, Button, Group, Loader, Pagination, Stack, Table, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { triggerUpdate } from "@utils/api-communication/RestApiCli";
import { getReleaseHeadsets } from "@utils/api-communication/HeadsetReleasesCli";
import {
  isOfferedStatus,
  type HeadsetProgressFilter,
  type HeadsetSelection,
  type ReleaseHeadsetPage,
  type ReleaseHeadsetRow,
} from "types/headsetReleaseTypes";
import { HeadsetStatusBadge } from "./HeadsetStatusBadge";
import { notifyError } from "./notifyError";

interface HeadsetListProps {
  version: string;
  selection: HeadsetSelection;
  status?: HeadsetProgressFilter;
  search?: string;
}

function lastSeen(row: ReleaseHeadsetRow, locale: string) {
  return row.lastSeenAt
    ? new Date(row.lastSeenAt).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })
    : "";
}

export function HeadsetList({ version, selection, status, search }: HeadsetListProps) {
  const { t, i18n } = useTranslation();
  const { entryId, groupBy, groupKey } = selection;
  const filterKey = [entryId, groupBy, groupKey, status, search].join("|");
  const [pageOfFilter, setPageOfFilter] = useState({ filterKey, page: 1 });
  const page = pageOfFilter.filterKey === filterKey ? pageOfFilter.page : 1;
  const setPage = (nextPage: number) => setPageOfFilter({ filterKey, page: nextPage });
  const [result, setResult] = useState<ReleaseHeadsetPage>();
  const [installing, setInstalling] = useState<string>();

  useEffect(() => {
    let current = true;
    getReleaseHeadsets(version, {
      entryId,
      groupBy,
      groupKey,
      status,
      q: search || undefined,
      page,
    })
      .then((headsets) => current && setResult(headsets))
      .catch(notifyError);
    return () => {
      current = false;
    };
  }, [version, entryId, groupBy, groupKey, status, search, page]);

  const installNow = (row: ReleaseHeadsetRow) => {
    setInstalling(row.pid);
    const finishInstalling = () =>
      setInstalling((current) => (current === row.pid ? undefined : current));
    triggerUpdate(
      { pid: row.pid, type: "app", version },
      (response) => {
        finishInstalling();
        const reachedHeadset = response?.data?.code === 1;
        notifications.show({
          color: "green",
          message: t(
            reachedHeadset ? "headsetReleases.installSent" : "headsetReleases.installSaved",
            {
              name: row.alias || row.serialNo,
            },
          ),
        });
      },
      (error) => {
        finishInstalling();
        notifyError(error);
      },
    );
  };

  if (!result) return <Loader size="sm" />;
  if (!result.total) {
    return (
      <Text size="sm" c="dimmed">
        {t("headsetReleases.noHeadsets")}
      </Text>
    );
  }

  return (
    <Stack gap="xs">
      <Table.ScrollContainer minWidth={860}>
        <Table verticalSpacing="xs" fz="sm" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("headsetReleases.column.headset")}</Table.Th>
              <Table.Th>{t("headsetReleases.column.model")}</Table.Th>
              <Table.Th>{t("headsetReleases.column.org")}</Table.Th>
              <Table.Th>{t("headsetReleases.column.version")}</Table.Th>
              <Table.Th>{t("headsetReleases.column.status")}</Table.Th>
              <Table.Th>{t("headsetReleases.column.lastSeen")}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {result.rows.map((row) => (
              <Table.Tr key={row.pid}>
                <Table.Td>
                  <Text fw={600} size="sm">
                    {row.alias || row.serialNo}
                  </Text>
                  <Text ff="monospace" size="xs" c="dimmed">
                    {row.serialNo}
                  </Text>
                </Table.Td>
                <Table.Td ff="monospace">{row.hardwareModel}</Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Text size="sm">{row.org?.name ?? "-"}</Text>
                    {row.otherOrgCount > 0 && (
                      <Badge variant="default" size="sm">
                        +{row.otherOrgCount}
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td fw={600}>{row.version ?? "-"}</Table.Td>
                <Table.Td>
                  <HeadsetStatusBadge status={row.status} reason={row.reason} />
                </Table.Td>
                <Table.Td c="dimmed">{lastSeen(row, i18n.language)}</Table.Td>
                <Table.Td>
                  {row.online && isOfferedStatus(row.status) && (
                    <Button
                      size="compact-xs"
                      variant="light"
                      loading={installing === row.pid}
                      onClick={() => installNow(row)}
                    >
                      {t("headsetReleases.installNow")}
                    </Button>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {result.total > result.pageSize && (
        <Group justify="flex-end">
          <Pagination
            size="sm"
            value={page}
            onChange={setPage}
            total={Math.ceil(result.total / result.pageSize)}
          />
        </Group>
      )}
    </Stack>
  );
}
